import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BookOpen, Heart, Star, Trophy, Play, Plus, 
  BarChart3, Calendar, LogOut, Cross,
  Check, Music, Loader2, ChevronDown, ChevronRight, Lock, ArrowRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ChristianParentDashboard = () => {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [childProgress, setChildProgress] = useState(null);
  const [activeTab, setActiveTab] = useState('children');
  const [worksheetHistory, setWorksheetHistory] = useState(null);
  const [loadingWorksheets, setLoadingWorksheets] = useState(false);
  const [curriculum, setCurriculum] = useState(null);
  const [expandedQuarter, setExpandedQuarter] = useState(null);
  const [loadingCurriculum, setLoadingCurriculum] = useState(false);
  const [completedWeeks, setCompletedWeeks] = useState(new Set());

  useEffect(() => {
    fetchChildren();
  }, []);

  useEffect(() => {
    if (selectedChild) {
      fetchChildProgress(selectedChild.id);
    }
  }, [selectedChild]);

  const fetchChildren = async () => {
    try {
      const token = localStorage.getItem('littlelearners-token') || localStorage.getItem('token');
      if (!token) {
        navigate('/christian/login');
        return;
      }
      const response = await axios.get(`${API_URL}/api/christian/children`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChildren(response.data);
      if (response.data.length > 0) {
        setSelectedChild(response.data[0]);
      }
    } catch (error) {
      console.error('Failed to fetch children:', error);
      if (error.response?.status === 401) {
        navigate('/christian/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchChildProgress = async (childId) => {
    try {
      const token = localStorage.getItem('littlelearners-token') || localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/christian/child/${childId}/progress`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChildProgress(response.data);
    } catch (error) {
      console.error('Failed to fetch progress:', error);
    }
  };

  const fetchWorksheetHistory = async (childId) => {
    try {
      setLoadingWorksheets(true);
      const token = localStorage.getItem('littlelearners-token') || localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/christian/worksheet/history/${childId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWorksheetHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch worksheet history:', error);
    } finally {
      setLoadingWorksheets(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'worksheets' && selectedChild) {
      fetchWorksheetHistory(selectedChild.id);
    }
    if (activeTab === 'schedule') {
      fetchCurriculum();
    }
  }, [activeTab, selectedChild]);

  const fetchCurriculum = async () => {
    if (curriculum) return;
    try {
      setLoadingCurriculum(true);
      const token = localStorage.getItem('littlelearners-token') || localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/christian/curriculum`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurriculum(response.data);
    } catch (error) {
      console.error('Failed to fetch curriculum:', error);
    } finally {
      setLoadingCurriculum(false);
    }
  };

  useEffect(() => {
    if (childProgress?.lessons) {
      const completed = new Set(
        childProgress.lessons
          .filter(l => l.lesson_completed)
          .map(l => l.week_number)
      );
      setCompletedWeeks(completed);
    }
  }, [childProgress]);

  const getLevelColor = (level) => {
    const colors = {
      seeds: 'from-green-400 to-emerald-500',
      roots: 'from-blue-400 to-indigo-500',
      warriors: 'from-purple-400 to-pink-500'
    };
    return colors[level] || colors.seeds;
  };

  const getLevelEmoji = (level) => {
    const emojis = { seeds: '🌱', roots: '🌿', warriors: '⚔️' };
    return emojis[level] || '🌱';
  };

  const handleLogout = () => {
    logout();
    navigate('/christian');
  };

  // Weekly schedule
  const weeklySchedule = [
    { day: 'Sunday', icon: '⛪', activity: 'Church Learning', color: 'bg-indigo-500' },
    { day: 'Wednesday', icon: '📖', activity: 'Bible Study', color: 'bg-emerald-500' },
    { day: 'Friday', icon: '🙏', activity: 'Prayer Night', color: 'bg-purple-500' }
  ];

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-indigo-950 to-purple-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-purple-400 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-indigo-950 to-purple-950">
      {/* Header */}
      <header className="bg-slate-900/80 backdrop-blur-md border-b border-white/10 p-4">
        <div className="flex items-center justify-between max-w-4xl mx-auto">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Cross className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Parent Dashboard</h1>
              <p className="text-xs text-purple-400">Christian Education</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-400 hover:text-white">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </header>

      {/* Tabs */}
      <div className="border-b border-white/10">
        <div className="max-w-4xl mx-auto flex">
          {['children', 'schedule', 'progress', 'worksheets'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-3 text-sm font-medium capitalize transition-colors ${
                activeTab === tab
                  ? 'text-purple-400 border-b-2 border-purple-500'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto p-4">
        {/* Children Tab */}
        {activeTab === 'children' && (
          <div className="space-y-4">
            {/* Children List */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-white">My Children</h2>
              <Button 
                size="sm" 
                onClick={() => navigate('/christian/add-child')}
                className="bg-gradient-to-r from-indigo-500 to-purple-600"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Child
              </Button>
            </div>

            {children.length === 0 ? (
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-8 text-center">
                  <div className="text-5xl mb-4">👶</div>
                  <h3 className="text-xl font-bold text-white mb-2">Add Your First Child</h3>
                  <p className="text-slate-400 mb-6">Get started by adding a child to begin their faith journey</p>
                  <Button onClick={() => navigate('/christian/add-child')} className="bg-gradient-to-r from-indigo-500 to-purple-600">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Child
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {children.map((child) => (
                  <Card 
                    key={child.id} 
                    className={`bg-slate-800/50 border cursor-pointer transition-all ${
                      selectedChild?.id === child.id ? 'border-purple-500' : 'border-slate-700 hover:border-slate-600'
                    }`}
                    onClick={() => setSelectedChild(child)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${getLevelColor(child.level)} flex items-center justify-center text-2xl`}>
                            {getLevelEmoji(child.level)}
                          </div>
                          <div>
                            <h3 className="font-bold text-white">{child.name}</h3>
                            <p className="text-sm text-purple-400 capitalize">Level: {child.level}</p>
                            <div className="flex items-center gap-3 mt-1">
                              <span className="text-xs text-slate-400 flex items-center gap-1">
                                <Star className="w-3 h-3 text-amber-400" />
                                {child.total_xp || 0} XP
                              </span>
                              <span className="text-xs text-slate-400 flex items-center gap-1">
                                <BookOpen className="w-3 h-3 text-emerald-400" />
                                Week {child.current_week || 1}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); navigate(`/christian/child/${child.id}`); }}
                            className="bg-gradient-to-r from-indigo-500 to-purple-600"
                          >
                            <Play className="w-4 h-4 mr-1" />
                            Learn
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Selected Child Stats */}
            {selectedChild && (
              <div className="mt-6 space-y-4">
                <h3 className="font-bold text-white">Quick Stats: {selectedChild.name}</h3>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Card className="bg-emerald-500/20 border-emerald-500/30">
                    <CardContent className="p-4 text-center">
                      <div className="text-2xl mb-1">📚</div>
                      <p className="text-lg font-bold text-emerald-400">{selectedChild.lessons_completed || 0}</p>
                      <p className="text-xs text-slate-400">Lessons</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-purple-500/20 border-purple-500/30">
                    <CardContent className="p-4 text-center">
                      <div className="text-2xl mb-1">💜</div>
                      <p className="text-lg font-bold text-purple-400">{selectedChild.scriptures_memorized || 0}</p>
                      <p className="text-xs text-slate-400">Verses</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-amber-500/20 border-amber-500/30">
                    <CardContent className="p-4 text-center">
                      <div className="text-2xl mb-1">⭐</div>
                      <p className="text-lg font-bold text-amber-400">{selectedChild.total_xp || 0}</p>
                      <p className="text-xs text-slate-400">XP Earned</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-blue-500/20 border-blue-500/30">
                    <CardContent className="p-4 text-center">
                      <div className="text-2xl mb-1">🏆</div>
                      <p className="text-lg font-bold text-blue-400">{selectedChild.badges?.length || 0}</p>
                      <p className="text-xs text-slate-400">Badges</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-2 gap-3">
                  <Button 
                    variant="outline" 
                    className="h-auto py-4 flex flex-col items-center gap-2 border-slate-700 text-white hover:bg-slate-800"
                    onClick={() => setActiveTab('progress')}
                    data-testid="view-progress-btn"
                  >
                    <BarChart3 className="w-6 h-6 text-blue-400" />
                    <span className="text-sm">View Progress</span>
                  </Button>
                  <Button 
                    variant="outline" 
                    className="h-auto py-4 flex flex-col items-center gap-2 border-slate-700 text-white hover:bg-slate-800"
                    onClick={() => navigate('/christian/songs')}
                    data-testid="worship-songs-btn"
                  >
                    <Music className="w-6 h-6 text-emerald-400" />
                    <span className="text-sm">Worship Songs</span>
                  </Button>
                </div>

                {/* Picture Login Code */}
                {selectedChild.picture_login_code && (
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-slate-400 mb-1">Child Login Code</p>
                          <p className="text-2xl font-mono font-bold text-purple-400 tracking-wider">
                            {selectedChild.picture_login_code}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-slate-400 mb-1">Picture Password</p>
                          <div className="flex gap-1">
                            {selectedChild.picture_login_images?.map((img, i) => {
                              const pictureMap = {
                                lion: '🦁', elephant: '🐘', rainbow: '🌈', star: '⭐',
                                heart: '❤️', sun: '☀️', moon: '🌙', flower: '🌸',
                                tree: '🌳', fish: '🐟', bird: '🐦', butterfly: '🦋',
                                crown: '👑', cross: '✝️', dove: '🕊️', lamb: '🐑'
                              };
                              return (
                                <span key={i} className="text-xl">{pictureMap[img] || '❓'}</span>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                      <p className="text-xs text-slate-500 mt-2">
                        Your child can log in at <span className="text-purple-400">Kid Login</span> using this code and their picture password
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </div>
        )}

        {/* Schedule Tab */}
        {activeTab === 'schedule' && (
          <div className="space-y-5">
            {!selectedChild && (
              <Card className="bg-amber-900/20 border-amber-500/30">
                <CardContent className="p-4 flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-amber-400 flex-shrink-0" />
                  <p className="text-sm text-amber-300">Select a child from the <button onClick={() => setActiveTab('children')} className="underline font-medium">Children tab</button> to see their progress and access lessons.</p>
                </CardContent>
              </Card>
            )}

            <h2 className="text-lg font-bold text-white" data-testid="schedule-heading">Weekly Schedule</h2>
            <p className="text-slate-400 text-sm mb-4">Structured learning throughout the week</p>

            <div className="space-y-3">
              {weeklySchedule.map((item, idx) => (
                <Card 
                  key={idx} 
                  className={`bg-slate-800/50 border-slate-700 transition-all ${selectedChild ? 'cursor-pointer hover:border-purple-500/50 hover:bg-slate-800/80' : ''}`}
                  onClick={() => {
                    if (selectedChild) {
                      navigate(`/christian/lesson/${selectedChild.id}/${selectedChild.current_week || 1}`);
                    }
                  }}
                  data-testid={`schedule-day-${item.day.toLowerCase()}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className={`w-14 h-14 rounded-xl ${item.color} flex items-center justify-center text-2xl`}>
                        {item.icon}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-white">{item.day}</h3>
                        <p className="text-sm text-slate-400">{item.activity}</p>
                      </div>
                      {selectedChild && (
                        <ArrowRight className="w-5 h-5 text-slate-500" />
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Interactive 52-Week Journey */}
            <Card className="bg-slate-800/50 border-slate-700 mt-6" data-testid="curriculum-journey">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-purple-400" />
                  52-Week Journey
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {loadingCurriculum ? (
                  <div className="flex justify-center py-6">
                    <Loader2 className="w-6 h-6 text-purple-400 animate-spin" />
                  </div>
                ) : (
                  [
                    { key: 'quarter_1', label: 'Q1', name: 'Creation & Beginnings', weeks: '1-13', theme: 'Trust God', color: 'from-blue-500/30 to-indigo-500/30', accent: 'text-blue-400' },
                    { key: 'quarter_2', label: 'Q2', name: 'God Builds a Nation', weeks: '14-26', theme: 'Obedience', color: 'from-emerald-500/30 to-teal-500/30', accent: 'text-emerald-400' },
                    { key: 'quarter_3', label: 'Q3', name: 'Kings & Prophets', weeks: '27-39', theme: 'Courage', color: 'from-amber-500/30 to-orange-500/30', accent: 'text-amber-400' },
                    { key: 'quarter_4', label: 'Q4', name: 'Jesus & The Church', weeks: '40-52', theme: 'Love & Service', color: 'from-rose-500/30 to-pink-500/30', accent: 'text-rose-400' }
                  ].map((q) => {
                    const isExpanded = expandedQuarter === q.key;
                    const quarterData = curriculum?.[q.key];
                    const qProgress = childProgress?.quarter_progress?.[q.key];
                    return (
                      <div key={q.key} data-testid={`quarter-${q.label.toLowerCase()}`}>
                        <button
                          onClick={() => setExpandedQuarter(isExpanded ? null : q.key)}
                          className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-slate-700/40 transition-colors"
                          data-testid={`quarter-${q.label.toLowerCase()}-toggle`}
                        >
                          <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${q.color} flex items-center justify-center text-sm font-bold ${q.accent}`}>
                            {q.label}
                          </div>
                          <div className="flex-1 text-left">
                            <p className="text-white text-sm font-medium">{q.name}</p>
                            <div className="flex items-center gap-2">
                              <p className="text-xs text-slate-400">Weeks {q.weeks} &bull; {q.theme}</p>
                              {qProgress && (
                                <span className="text-xs text-emerald-400">{qProgress.completed}/{qProgress.total} done</span>
                              )}
                            </div>
                          </div>
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-slate-400" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-slate-400" />
                          )}
                        </button>

                        {isExpanded && quarterData && (
                          <div className="ml-4 mt-1 space-y-1 border-l-2 border-slate-700 pl-4 pb-2">
                            {quarterData.weeks.map((week) => {
                              const isCompleted = completedWeeks.has(week.week);
                              const isCurrent = selectedChild && (selectedChild.current_week || 1) === week.week;
                              return (
                                <button
                                  key={week.week}
                                  onClick={() => {
                                    if (selectedChild) {
                                      navigate(`/christian/lesson/${selectedChild.id}/${week.week}`);
                                    } else {
                                      toast.info('Select a child first to access lessons');
                                    }
                                  }}
                                  className={`w-full flex items-center gap-3 p-2.5 rounded-lg text-left transition-all ${
                                    isCurrent 
                                      ? 'bg-purple-500/20 border border-purple-500/40' 
                                      : 'hover:bg-slate-700/30'
                                  } ${selectedChild ? 'cursor-pointer' : 'cursor-default'}`}
                                  data-testid={`week-${week.week}`}
                                >
                                  <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${
                                    isCompleted 
                                      ? 'bg-emerald-500/30 text-emerald-400' 
                                      : isCurrent 
                                        ? 'bg-purple-500/30 text-purple-400 ring-2 ring-purple-500/50' 
                                        : 'bg-slate-700/50 text-slate-500'
                                  }`}>
                                    {isCompleted ? <Check className="w-3.5 h-3.5" /> : week.week}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className={`text-sm font-medium truncate ${isCompleted ? 'text-emerald-300' : isCurrent ? 'text-white' : 'text-slate-300'}`}>
                                      {week.topic}
                                    </p>
                                    <p className="text-xs text-slate-500 truncate">{week.scripture}</p>
                                  </div>
                                  {isCurrent && (
                                    <span className="text-[10px] font-medium bg-purple-500/30 text-purple-300 px-2 py-0.5 rounded-full flex-shrink-0">Current</span>
                                  )}
                                  {selectedChild && (
                                    <ArrowRight className="w-4 h-4 text-slate-600 flex-shrink-0" />
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Progress Tab */}
        {activeTab === 'progress' && selectedChild && (
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-white">{selectedChild.name}'s Progress</h2>

            {/* Quarter Progress */}
            {childProgress?.quarter_progress && (
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(childProgress.quarter_progress).map(([key, data]) => (
                  <Card key={key} className="bg-slate-800/50 border-slate-700">
                    <CardContent className="p-4">
                      <p className="text-sm text-slate-400 mb-2">{key.replace('_', ' ').toUpperCase()}</p>
                      <Progress value={(data.completed / data.total) * 100} className="h-2 mb-2" />
                      <p className="text-xs text-emerald-400">{data.completed}/{data.total} lessons</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Scriptures Memorized */}
            <Card className="bg-gradient-to-br from-purple-900/30 to-indigo-900/30 border-purple-500/30">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Heart className="w-5 h-5 text-purple-400" />
                  Scripture Memory Progress
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-slate-300">Verses Memorized</span>
                  <span className="text-2xl font-bold text-purple-400">
                    {selectedChild.scriptures_memorized || 0}/50
                  </span>
                </div>
                <Progress value={Math.min(((selectedChild.scriptures_memorized || 0) / 50) * 100, 100)} className="h-3" />
                <p className="text-xs text-slate-400 mt-2">
                  50 verses needed for graduation milestone
                </p>
              </CardContent>
            </Card>

            {/* Graduation Requirements */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-amber-400" />
                  Graduation Milestones
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-slate-300 text-sm">50 Scriptures Memorized</span>
                  {(selectedChild.scriptures_memorized || 0) >= 50 ? (
                    <Check className="w-5 h-5 text-emerald-400" />
                  ) : (
                    <span className="text-xs text-slate-500">{selectedChild.scriptures_memorized || 0}/50</span>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-300 text-sm">Can Pray Publicly</span>
                  {selectedChild.graduation_requirements?.can_pray_publicly ? (
                    <Check className="w-5 h-5 text-emerald-400" />
                  ) : (
                    <span className="text-xs text-slate-500">Not yet</span>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-300 text-sm">Bible Stories Knowledge</span>
                  <span className="text-xs text-slate-500">{selectedChild.lessons_completed || 0} stories</span>
                </div>
              </CardContent>
            </Card>

            {/* Recent Scriptures */}
            {childProgress?.scriptures?.length > 0 && (
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white text-base">Recently Memorized</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {childProgress.scriptures.slice(0, 5).map((verse, idx) => (
                    <div key={idx} className="bg-slate-700/30 rounded-lg p-3">
                      <p className="text-sm text-purple-400 mb-1">{verse.verse_reference}</p>
                      <p className="text-xs text-slate-300 line-clamp-2">{verse.verse_text}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Worksheets Tab */}
        {activeTab === 'worksheets' && selectedChild && (
          <div className="space-y-4" data-testid="worksheets-tab">
            <h2 className="text-lg font-bold text-white">{selectedChild.name}'s Worksheets</h2>

            {loadingWorksheets ? (
              <div className="flex justify-center py-8">
                <div className="w-6 h-6 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : worksheetHistory ? (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 gap-3">
                  <Card className="bg-emerald-900/20 border-emerald-500/30">
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-emerald-400">{worksheetHistory.summary?.total_completed || 0}</p>
                      <p className="text-xs text-slate-400">Completed</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-amber-900/20 border-amber-500/30">
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-amber-400">{worksheetHistory.summary?.average_score || 0}%</p>
                      <p className="text-xs text-slate-400">Avg Score</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-indigo-900/20 border-indigo-500/30">
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-indigo-400">+{worksheetHistory.summary?.total_xp_earned || 0}</p>
                      <p className="text-xs text-slate-400">XP Earned</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-pink-900/20 border-pink-500/30">
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-pink-400">{worksheetHistory.summary?.perfect_scores || 0}</p>
                      <p className="text-xs text-slate-400">Perfect Scores</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Worksheet History List */}
                {worksheetHistory.worksheets?.length > 0 ? (
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-white text-base flex items-center gap-2">
                        <BookOpen className="w-4 h-4 text-indigo-400" />
                        Completed Worksheets
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {worksheetHistory.worksheets.map((ws, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-slate-700/30 rounded-lg p-3">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-white font-medium truncate">Week {ws.week_number}: {ws.topic}</p>
                            <p className="text-xs text-slate-400">{new Date(ws.completed_at).toLocaleDateString()}</p>
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                            <div className={`text-sm font-bold ${ws.score >= 90 ? 'text-emerald-400' : ws.score >= 70 ? 'text-amber-400' : ws.score >= 50 ? 'text-orange-400' : 'text-red-400'}`}>
                              {ws.score}%
                            </div>
                            <span className="text-xs text-indigo-400">+{ws.xp_earned} XP</span>
                            {ws.score === 100 && <Star className="w-4 h-4 text-amber-400" />}
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardContent className="p-8 text-center">
                      <BookOpen className="w-10 h-10 mx-auto mb-3 text-slate-600" />
                      <p className="text-slate-400 text-sm">No worksheets completed yet</p>
                      <p className="text-slate-500 text-xs mt-1">Worksheets will appear here as {selectedChild.name} completes them</p>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-6 text-center">
                  <p className="text-slate-400">Select a child to view their worksheet history</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChristianParentDashboard;
